import { useCallback, useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { Button, useBreakpoints, ContentCard } from "@narmi/design_system";
import { entities } from "byzantine";
import useCardLinkedOffersFeature from "../../CardLinkedOffers/hooks";
import CompactOffersGrid from "../../CardLinkedOffers/CompactOffersGrid";
import CardOfferDetailsDrawer from "../../CardLinkedOffers/CardOfferDetailsDrawer";
import styles from "./CardOffers.module.scss";

interface CardOffersEnrolledProps {
  maxOffers?: number;
}

const CardOffersEnrolled = ({ maxOffers = 8 }: CardOffersEnrolledProps) => {
  const { l10n } = useLocalization();
  const { m } = useBreakpoints();
  const navigate = useNavigate();
  const isCardLinkedOffersEnabled = useCardLinkedOffersFeature();
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Use redux selectors to get data
  const offers = entities.cardLinkedOffers.useAllCardLinkedOffers();

  // Data fetching hooks
  const { fetchOffers } = entities.cardLinkedOffers.useFetchCardLinkedOffers();

  // Fetch offers data on mount
  useEffect(() => {
    if (isCardLinkedOffersEnabled) {
      fetchOffers(false); // Available offers
    }
  }, [isCardLinkedOffersEnabled, fetchOffers]);

  const handleViewAllOffers = useCallback(() => {
    navigate("/cards/offers");
  }, [navigate]);

  const handleOfferClick = useCallback((offerId: string) => {
    setSelectedOfferId(offerId);
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerDismiss = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedOfferId(undefined);
  }, []);

  // Don't render if feature flag is disabled
  if (!isCardLinkedOffersEnabled) {
    return null;
  }

  const displayOffers = offers.slice(0, maxOffers);

  return (
    <div>
      <ContentCard kind="elevated">
        <div className={styles.container}>
          {/* Header with title and button */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.iconAndTitle}>
                <div className={styles.iconContainer}>
                  <span className="narmi-icon-credit-card" />
                </div>
                <div className={`fontWeight--bold ${styles.title}`}>
                  {l10n.getString(
                    "card-offers-homepage-title",
                    null,
                    "Card offers"
                  )}
                </div>
              </div>
              <Button
                label={l10n.getString(
                  "card-offers-homepage-view-offers",
                  null,
                  "View offers"
                )}
                testId="view-offers"
                onClick={handleViewAllOffers}
                kind={m ? "primary" : "plain"}
                size={"xs"}
              />
            </div>
          </div>

          {/* Description */}
          <div className={styles.description}>
            <div className="fontSize--s fontWeight--normal">
              {l10n.getString(
                "card-offers-homepage-description",
                null,
                "Turn everyday purchases into rewards. Activate cash back on your card and start earning money back on dining, groceries, gas, and more."
              )}
            </div>
          </div>

          {/* Offers Grid */}
          <div className={styles.offersSection}>
            <CompactOffersGrid
              offers={displayOffers}
              onOfferClick={handleOfferClick}
            />
          </div>
        </div>
      </ContentCard>

      {/* Offer Details Drawer */}
      <CardOfferDetailsDrawer
        isOpen={isDrawerOpen}
        offerId={selectedOfferId}
        onOfferClick={handleOfferClick}
        onUserDismiss={handleDrawerDismiss}
      />
    </div>
  );
};

export default CardOffersEnrolled;
